import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import SEOHead from '../../components/SEO/SEOHead';
import defaultSEO from '../../components/SEO/seoConfig';
import { 
  CalendarIcon, 
  ClockIcon, 
  MapPinIcon, 
  UserGroupIcon, 
  StarIcon,
  HeartIcon,
  ShareIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid, HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';

import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import TourGallery from './components/TourGallery';
import TourItinerary from './components/TourItinerary';
import TourMap from './components/TourMap';
import TourReviews from './components/TourReviews';
import RelatedTours from './components/RelatedTours';
import BookingForm from './components/BookingForm';
import TourAvailabilityCalendar from './components/TourAvailabilityCalendar';
import TourComparison from './components/TourComparison';
import WishlistModal from './components/WishlistModal';
import VehicleInfo from './components/VehicleInfo';
import TourVehicleInfo from '../../components/Tours/TourVehicleInfo';
import Box from '@mui/material/Box'; // Update the Box import to fix the ESLint error

const TourDetailPage = () => {
  const { slug } = useParams(); // Changed from 'id' to 'slug' to match the route parameter
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('description');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [showWishlistModal, setShowWishlistModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  
  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('token');

  // Fetch tour details
  const fetchTourDetails = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const api = axios.create({
      baseURL: apiUrl
    });
    
    try {
      let response;
      
      // Check if the slug is a number (tour ID) or a string (actual slug)
      if (!isNaN(slug)) {
        // If it's a number, fetch by ID
        response = await api.get(`/api/v1/tours/${slug}`);
      } else {
        // If it's a string, fetch by slug
        response = await api.get(`/api/v1/tours/slug/${slug}`);
      }
      
      // Make sure we're accessing the data property from the API response
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching tour details:', error);
      throw error;
    }
  };

  const { data: tour, isLoading, error, refetch } = useQuery(
    ['tour', slug], // Updated query key to use slug instead of id
    fetchTourDetails,
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Helper function to parse JSON fields
  const parseJsonField = (field) => {
    if (!field) return [];
    if (typeof field === 'string') {
      try {
        return JSON.parse(field);
      } catch (e) {
        console.error('Error parsing JSON field:', e, field);
        return [];
      }
    }
    return field;
  };
  
  // Debug the itinerary data
  console.log('Tour itinerary data:', tour?.itinerary || 'No itinerary data');
  
  // Helper function to format location data
  const formatLocation = (location) => {
    if (typeof location === 'object' && location !== null) {
      return location.name ? 
        `${location.name}${location.region ? ` - ${location.region}` : ''}${location.country ? `, ${location.country}` : ''}` 
        : 'Location not specified';
    }
    return location || 'Location not specified';
  };

  // Log the tour data for debugging
  console.log('Tour data from API:', tour);
  
  // Use fallback data when in development or when there's an error
  const tourData = tour?.data || tour || {
    id: 0, // Use a default ID since we don't have one
    slug: slug, // Use the slug from the URL
    name: 'Tour Information',
    description: 'Tour description will appear here.',
    price: 0,
    duration: 1,
    duration_type: 'days',
    included_services: [],
    excluded_services: [],
    itinerary: [],
    location: 'Location',
    featured_image: '',
    gallery_images: [],
    included_services: [],
    excluded_services: [],
    itinerary: [],
    group_size: 1,
    rating: 0,
    reviewCount: 0,
    categories: []
  };

  // Check if tour is in favorites
  const checkFavoriteStatus = async (tourId) => {
    if (!isLoggedIn) return;
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await axios.get(`${apiUrl}/api/v1/user/favorites/check/${tourId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setIsFavorite(response.data.data.isFavorite);
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  };

  // Handle save to favorites
  const handleSaveToFavorites = async () => {
    if (!isLoggedIn) {
      // Redirect to login if not logged in
      localStorage.setItem('redirectAfterLogin', `/tours/${slug}`);
      navigate('/login');
      return;
    }
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const tourId = tourData.id;
      
      if (isFavorite) {
        // Remove from favorites
        await axios.delete(`${apiUrl}/api/v1/user/favorites/${tourId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setIsFavorite(false);
        alert('Tour removed from favorites!');
      } else {
        // Add to favorites
        await axios.post(`${apiUrl}/api/v1/user/favorites/${tourId}`, {}, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setIsFavorite(true);
        alert('Tour saved to favorites!');
      }
    } catch (error) {
      console.error('Error updating favorites:', error);
      alert('Failed to update favorites. Please try again.');
    }
  };

  // Handle share
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: tourData.title,
        text: `Check out this amazing tour: ${tourData.title}`,
        url: window.location.href,
      })
      .catch((error) => console.log('Error sharing', error));
    } else {
      // Fallback for browsers that don't support the Web Share API
      navigator.clipboard.writeText(window.location.href)
        .then(() => alert('Link copied to clipboard!'))
        .catch((error) => console.log('Error copying to clipboard', error));
    }
  };

  // Render star rating
  const renderStarRating = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<StarIconSolid key={i} className="h-5 w-5 text-yellow-500" />);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<StarIconSolid key={i} className="h-5 w-5 text-yellow-500" />);
      } else {
        stars.push(<StarIcon key={i} className="h-5 w-5 text-yellow-500" />);
      }
    }
    
    return stars;
  };

  if (isLoading) {
    return (
      <div className="container-custom py-20">
        <div className="flex justify-center items-center h-96">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-custom py-20">
        <ErrorMessage 
          message="Failed to load tour details. Please try again later." 
          onRetry={refetch} 
        />
      </div>
    );
  }

  // Generate SEO metadata for this specific tour
  const generateTourSEO = () => {
    if (!tourData) return null;
    
    // Extract tour highlights for keywords and description
    const highlights = parseJsonField(tourData.highlights || '[]').join(', ');
    
    // Format location for SEO
    const locationText = formatLocation(tourData.location);
    
    // Create a clean description without HTML tags
    const stripHtml = (html) => {
      if (!html) return '';
      return html.replace(/<[^>]*>/g, '');
    };
    
    const cleanDescription = stripHtml(tourData.description);
    const shortDescription = cleanDescription.length > 160 ? 
      `${cleanDescription.substring(0, 157)}...` : cleanDescription;
    
    // Generate keywords from tour data
    const keywords = [
      tourData.title,
      locationText,
      tourData.category?.name || '',
      'safari',
      'tour',
      'travel',
      highlights,
      `${tourData.duration} day tour`,
      `${tourData.duration} day safari`
    ].filter(Boolean);
    
    // Generate structured data for this tour
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: tourData.title,
      description: cleanDescription,
      image: tourData.featured_image || tourData.images?.[0] || '',
      sku: `TOUR-${tourData.id}`,
      mpn: `TOUR-${tourData.id}`,
      brand: {
        '@type': 'Brand',
        name: defaultSEO.siteName
      },
      offers: {
        '@type': 'Offer',
        url: `${defaultSEO.siteUrl}/tours/${tourData.slug}`,
        priceCurrency: 'USD',
        price: tourData.price,
        priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        availability: 'https://schema.org/InStock'
      },
      aggregateRating: tourData.rating ? {
        '@type': 'AggregateRating',
        ratingValue: tourData.rating,
        reviewCount: tourData.reviews_count || 1
      } : undefined
    };
    
    // If tour has reviews, add them to structured data
    if (tourData.reviews && tourData.reviews.length > 0) {
      structuredData.review = tourData.reviews.map(review => ({
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: review.rating
        },
        author: {
          '@type': 'Person',
          name: review.user?.name || 'Anonymous'
        },
        reviewBody: review.comment
      }));
    }
    
    return {
      title: tourData.title,
      description: shortDescription || `Explore ${tourData.title} in ${locationText}. ${tourData.duration}-day tour with professional guides.`,
      keywords: keywords,
      canonicalUrl: `${defaultSEO.siteUrl}/tours/${tourData.slug}`,
      ogType: 'product',
      ogImage: tourData.featured_image || tourData.images?.[0] || `${defaultSEO.siteUrl}/images/tours-default.jpg`,
      structuredData: structuredData
    };
  };
  
  // Get SEO data
  const tourSEO = generateTourSEO();

  return (
    <div className="tour-detail-page bg-gray-50 min-h-screen">
      {/* SEO Head */}
      {tourSEO && (
        <SEOHead
          title={tourSEO.title}
          description={tourSEO.description}
          keywords={tourSEO.keywords}
          canonicalUrl={tourSEO.canonicalUrl}
          ogType={tourSEO.ogType}
          ogImage={tourSEO.ogImage}
          structuredData={tourSEO.structuredData}
        />
      )}
      {/* Tour Gallery */}
      <TourGallery 
        images={[
          tourData.featured_image, 
          ...(Array.isArray(tourData.gallery_images) ? tourData.gallery_images : [])
        ]} 
        title={tourData.name || tourData.title} 
      />
      
      <div className="container-custom py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="lg:w-2/3">
            {/* Tour Header */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-3xl font-bold text-gray-800 mb-2">{tourData.title}</h1>
                  <div className="flex items-center mb-4">
                    <div className="flex items-center mr-4">
                      {renderStarRating(tourData.rating)}
                      <span className="ml-2 text-gray-600">({tourData.reviewCount} reviews)</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <MapPinIcon className="h-5 w-5 mr-1 text-primary" />
                      {formatLocation(tourData.location)}
                    </div>
                  </div>
                  
                  {/* Categories */}
                  {tourData.categories && tourData.categories.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {tourData.categories.map((category, index) => (
                        <span 
                          key={category.id || index} 
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-light text-primary"
                        >
                          {category.name || category}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleSaveToFavorites}
                    className={`flex items-center justify-center p-2 rounded-full ${isFavorite ? 'bg-red-50' : 'bg-white'} shadow-md hover:bg-gray-100`}
                    aria-label={isFavorite ? 'Remove from favorites' : 'Save to favorites'}
                  >
                    {isFavorite ? (
                      <HeartIconSolid className="h-6 w-6 text-red-500" />
                    ) : (
                      <HeartIcon className="h-6 w-6 text-gray-600" />
                    )}
                  </button>
                  <button 
                    onClick={handleShare}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-300"
                    aria-label="Share tour"
                  >
                    <ShareIcon className="h-6 w-6 text-gray-600" />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 border-t border-gray-200 pt-6">
                <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg">
                  <ClockIcon className="h-6 w-6 text-primary mb-2" />
                  <span className="text-sm text-gray-500">Duration</span>
                  <span className="font-semibold">{tourData.duration} days</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg">
                  <UserGroupIcon className="h-6 w-6 text-primary mb-2" />
                  <span className="text-sm text-gray-500">Group Size</span>
                  <span className="font-semibold">Max {tourData.group_size || tourData.groupSize || 'N/A'} people</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg">
                  <CurrencyDollarIcon className="h-6 w-6 text-primary mb-2" />
                  <span className="text-sm text-gray-500">Price</span>
                  <span className="font-semibold">${tourData.price} per person</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg">
                  <ShieldCheckIcon className="h-6 w-6 text-primary mb-2" />
                  <span className="text-sm text-gray-500">Tour Type</span>
                  <span className="font-semibold">
                    {Array.isArray(tourData.categories) && tourData.categories.length > 0 
                      ? tourData.categories.map(cat => cat.name || cat).join(', ')
                      : tourData.category || 'Adventure'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Tour Tabs */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
              <div className="mt-8 border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  <button
                    className={`${
                      activeTab === 'description'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                    onClick={() => setActiveTab('description')}
                  >
                    Description
                  </button>
                  <button
                    className={`${
                      activeTab === 'itinerary'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                    onClick={() => setActiveTab('itinerary')}
                  >
                    Itinerary
                  </button>
                  <button
                    className={`${
                      activeTab === 'map'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                    onClick={() => setActiveTab('map')}
                  >
                    Map
                  </button>
                  <button
                    className={`${
                      activeTab === 'reviews'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                    onClick={() => setActiveTab('reviews')}
                  >
                    Reviews
                  </button>
                  <button
                    className={`${
                      activeTab === 'vehicles'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                    onClick={() => setActiveTab('vehicles')}
                  >
                    Vehicles
                  </button>
                </nav>
              </div>
              
              <div className="mt-8">
                {activeTab === 'description' && (
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-6">Tour Description</h3>
                    <div 
                      className="prose max-w-none mb-8" 
                      dangerouslySetInnerHTML={{ __html: tourData.description || tourData.desc || '' }}
                    />
                    
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h3 className="text-lg font-semibold mb-4">What's Included</h3>
                        <ul className="space-y-2">
                          {parseJsonField(tourData.included_services).map((item, index) => (
                            <li key={index} className="flex items-start">
                              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-100 text-green-500 mr-3">
                                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Not Included</h3>
                        <ul className="space-y-2">
                          {parseJsonField(tourData.excluded_services).map((item, index) => (
                            <li key={index} className="flex items-start">
                              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-100 text-red-500 mr-3">
                                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeTab === 'itinerary' && (
                  <>
                    {console.log('Rendering itinerary tab with data:', parseJsonField(tourData.itinerary))}
                    <TourItinerary itinerary={parseJsonField(tourData.itinerary)} />
                  </>
                )}
                
                {activeTab === 'map' && (
                  <>
                    {console.log('Rendering map tab with location data:', tourData.location, tourData.map_location)}
                    <TourMap 
                      location={tourData.map_location || tourData.mapLocation || tourData.location || tourData.coordinates} 
                      title={tourData.title || tourData.name} 
                    />
                  </>
                )}
                
                {activeTab === 'reviews' && (
                  <TourReviews reviews={tourData.reviews || []} rating={tourData.rating || 0} tourId={tourData.id} />
                )}
                
                {activeTab === 'vehicles' && (
                  <div>
                    <Box sx={{ mt: 4 }}>
                      <TourVehicleInfo tourId={tourData.id} />
                    </Box>
                  </div>
                )}
              </div>
            </div>
            
            {/* Related Tours */}
            <RelatedTours tours={tourData.relatedTours || []} />
          </div>
          
          {/* Sidebar - Booking Form */}
          <div className="lg:w-1/3">
            <div className="sticky top-24">
              <BookingForm tour={tourData} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourDetailPage;
